import { ModalService, SimpleMessageService, WebAppCommonModule } from "@allbesmart/web-app-common";
import { UserRole, WebAppKeycloakAuthModule } from "@allbesmart/web-app-keycloak-auth";
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { OrderType } from "src/app/enums/order-type";
import {
  BillOfMaterialsItem,
  OrderDto,
  PutOrderDto,
} from "src/app/models/order-dto";
import { OrderService } from "src/app/services/order.service";
import { DeleteOrderWarningModalComponent } from "../../orders/delete-order-warning-modal/delete-order-warning-modal.component";
import { OrderLogsModalComponent } from "../../orders/order-logs-modal/order-logs-modal.component";
import { OperationItemModalComponent } from "../operation-item-modal/operation-item-modal.component";
import { OrderTenantAssignmentComponent } from "../order-tenant-assignment/order-tenant-assignment.component";
import { OrderTrackingDetailsConfigurationComponent } from "../order-tracking-details-configuration/order-tracking-details-configuration.component";
import { NgClass, DatePipe } from "@angular/common";
import { BillOfMaterialsProgressPipe } from "../../../pipes/bill-of-materials-progress.pipe";

@Component({
    selector: "oaibox-operation-item",
    templateUrl: "./operation-item.component.html",
    styleUrl: "./operation-item.component.css",
    imports: [
        WebAppKeycloakAuthModule,
        NgClass,
        WebAppCommonModule,
        DatePipe,
        BillOfMaterialsProgressPipe,
    ],
})
export class OperationItemComponent implements OnInit, OnDestroy {
  @Input({ required: true }) order!: OrderDto;

  protected readonly OrderType = OrderType;
  protected readonly UserRole = UserRole;

  protected bomIsExpanded: boolean = false;
  protected dueDateIsOverdue: boolean = false;

  private updateOrderSubscription?: Subscription;

  constructor(
    private readonly orderService: OrderService,
    private readonly simpleMessageService: SimpleMessageService,
    private readonly modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.bomIsExpanded =
      this.order.orderType === OrderType.AWAITING_PROCUREMENT;

    if (this.order.dueDate) {
      this.dueDateIsOverdue =
        this.order.orderType !== OrderType.BRINGUP_DONE &&
        new Date(this.order.dueDate).getTime() < Date.now();
    }
  }

  protected updateOrderBomItem(
    item: BillOfMaterialsItem,
    isAvailable: boolean
  ): void {
    const putOrderDto: PutOrderDto = {
      id: this.order.id,
      bom: (this.order.bom ?? []).map((bomItem) =>
        bomItem.name === item.name ? { ...bomItem, isAvailable } : bomItem
      ),
    };

    this.updateOrderSubscription?.unsubscribe();
    this.updateOrderSubscription = this.orderService
      .updateOrder(putOrderDto)
      .subscribe({
        next: () => this.orderService.refreshOrders(),
        error: () => {
          this.simpleMessageService.showSimpleMessage({
            message: "Failed to update Bill of Materials item!",
            icon: "fa-solid fa-triangle-exclamation",
            color: "danger",
          });
        },
      });
  }

  protected openOperationItemModal(): void {
    this.modalService.pushModal({
      component: OperationItemModalComponent,
      size: "medium",
      inputs: new Map<string, OrderDto>([["order", this.order]]),
    });
  }

  protected openOrderLogsModal(): void {
    this.modalService.pushModal({
      component: OrderLogsModalComponent,
      size: "medium",
      inputs: new Map<string, OrderDto>([["order", this.order]]),
    });
  }

  protected openDeleteOrderModal(): void {
    this.modalService.pushModal({
      component: DeleteOrderWarningModalComponent,
      inputs: new Map<string, OrderDto>([
        ["order", this.order]
      ]),
      closeButtonColor: "white",
    });
  }

  protected openOrderTenantAssignmentModal(): void {
    this.modalService.pushModal({
      component: OrderTenantAssignmentComponent,
      size: "medium",
      inputs: new Map<string, OrderDto>([["order", this.order]]),
    });
  }

  protected openOrderTrackingDetailsConfigurationModal(): void {
    this.modalService.pushModal({
      component: OrderTrackingDetailsConfigurationComponent,
      size: "medium",
      inputs: new Map<string, OrderDto>([["order", this.order]]),
    });
  }

  ngOnDestroy(): void {
    this.updateOrderSubscription?.unsubscribe();
  }
}
